import { date as fmtDate, num, pct } from '@/lib/format';
import { Panel } from '@/components/ui/Panel';
import { StatusChip } from '@/components/ui/StatusChip';
import { useRpc } from '@/pages/company/useRpc';

export type OperationKey = 'quarry' | 'concrete' | 'brick' | 'precast';
type Rag = 'green' | 'amber' | 'red' | 'grey';

export interface OperationUnit {
  operation: OperationKey;
  site_id: string;
  code: string;
  name: string;
  measure: 't' | 'm³' | 'pcs';
  produced: number | null;
  day_produced: number | null;
  target_month: number | null;
  target_to_date: number | null;
  target_basis: 'pro_rata' | 'full_month' | null;
  achievement_pct: number | null;
  achievement_rag: Rag | null;
}

export interface OperationsSummary {
  period: { from: string; to: string };
  units: OperationUnit[];
}

const OPERATION_LABEL: Record<OperationKey, string> = {
  quarry: 'Quarry',
  concrete: 'Concrete',
  brick: 'Brick Plant',
  precast: 'Pre-cast',
};
const WHAT: Record<OperationKey, string> = {
  quarry: 'tonnes',
  concrete: 'm³ accepted',
  brick: 'good pieces',
  precast: 'good pieces',
};
/** Spans the whole row of the Dashboard's panel grid (harmless elsewhere). */
const WIDE = { gridColumn: '1 / -1' } as const;
const ORDER: OperationKey[] = ['quarry', 'concrete', 'brick', 'precast'];
const decimals = (u: OperationUnit) => (u.measure === 'm³' ? 1 : 0);


/** Totals per operation across its sites: produced, and achievement only
 *  over the sites that have a target (as plant_summary does). */
function totals(units: OperationUnit[]) {
  const by = new Map<OperationKey, OperationUnit[]>();
  for (const u of units) by.set(u.operation, [...(by.get(u.operation) ?? []), u]);
  return ORDER.filter((k) => by.has(k)).map((k) => {
    const rows = by.get(k)!;
    const targeted = rows.filter((u) => u.target_to_date != null && Number(u.target_to_date) > 0);
    const target = targeted.reduce((a, u) => a + Number(u.target_to_date), 0);
    const doneOnTarget = targeted.reduce((a, u) => a + Number(u.produced ?? 0), 0);
    const ach = target > 0 ? Math.round((1000 * doneOnTarget) / target) / 10 : null;
    const fullMonth = targeted.length > 0 && targeted.every((u) => u.target_basis === 'full_month');
    return {
      key: k, measure: rows[0].measure, sites: rows.length,
      produced: rows.reduce((a, u) => a + Number(u.produced ?? 0), 0),
      today: rows.reduce((a, u) => a + Number(u.day_produced ?? 0), 0),
      target: target > 0 ? target : null, ach, fullMonth,
    };
  });
}

const tone = (p: number | null, fullMonth: boolean) =>
  p == null || fullMonth ? 'var(--brand-teal)' : p >= 95 ? 'var(--fill-green)' : p >= 80 ? 'var(--fill-amber)' : 'var(--fill-red)';

/**
 * One headline card per operation (quarry, concrete, brick, pre-cast):
 * month to date across every site the user may see, against the target to
 * date, with the last day beneath. The table below breaks it down by site.
 */
function OperationCards({ units, isToday }: { units: OperationUnit[]; isToday: boolean }) {
  const t = totals(units);
  if (!t.length) return null;
  return (
    <section className="headline" aria-label="Production by operation">
      {t.map((c) => {
        const d = c.measure === 'm³' ? 1 : 0;
        const w = c.ach == null ? 0 : Math.max(0, Math.min(100, c.ach));
        return (
          <div className="headline__card" key={c.key} data-op-card={c.key}>
            <div className="stat__label">{OPERATION_LABEL[c.key]} · month to date</div>
            <div className="headline__value">{num(c.produced, d)}<span className="headline__unit"> {c.measure}</span></div>
            <div className="meter" role="img" aria-label={`${pct(c.ach)} % of the target to date`}>
              <span style={{ width: `${w}%`, background: tone(c.ach, c.fullMonth) }} />
            </div>
            <div className="headline__sub">
              {c.target != null
                ? `${pct(c.ach)} % of ${num(c.target, d)} ${c.measure} ${c.fullMonth ? 'this month' : 'to date'}`
                : 'No target set'}
              {` · ${isToday ? 'today' : 'that day'} ${num(c.today, d)} ${c.measure}`}
              {c.sites > 1 ? ` · ${c.sites} sites` : ''}
            </div>
          </div>
        );
      })}
    </section>
  );
}

/**
 * Every operation and site the user may see, month to date against its
 * target to date (operations_summary, 0069). Used on the Dashboard ("All
 * operations this month") and on the Company page. Each module's own rules
 * decide which rows come back; units measure different things, so there is
 * no grand total.
 */
export function AllOperations({ to, title = 'All operations this month', cards = false, isToday = true }: {
  to: string; title?: string; cards?: boolean; isToday?: boolean;
}) {
  const { data, stale, error } = useRpc<OperationsSummary>('operations_summary', { p_to: to });
  if (error) {
    return <Panel style={WIDE} title={title}><p className="small muted" style={{ margin: 0 }}>Could not load: {error}</p></Panel>;
  }
  if (!data) {
    return <Panel style={WIDE} title={title}><div className="centered"><span className="spinner" /></div></Panel>;
  }
  const units = (Array.isArray(data.units) ? data.units : [])
    .slice()
    .sort((a, b) => ORDER.indexOf(a.operation) - ORDER.indexOf(b.operation) || a.name.localeCompare(b.name));
  return (
    <>
    {cards && <OperationCards units={units} isToday={isToday} />}
    <Panel style={WIDE} title={title} className="all-operations"
      actions={stale ? <StatusChip tone="amber">Cached</StatusChip> : undefined}>
      {units.length === 0 ? (
        <p className="small muted" style={{ margin: 0 }}>No operation you work with has figures yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Operation</th>
                <th>Site</th>
                <th style={{ textAlign: 'right' }}>Last day</th>
                <th style={{ textAlign: 'right' }}>Month to date</th>
                <th style={{ textAlign: 'right' }}>Target to date</th>
                <th style={{ textAlign: 'right' }}>Month target</th>
                <th style={{ textAlign: 'right' }}>Achievement</th>
              </tr>
            </thead>
            <tbody>
              {units.map((u) => (
                <tr key={`${u.operation}-${u.site_id}`} data-op={u.operation} data-site={u.code}>
                  <td><strong>{OPERATION_LABEL[u.operation]}</strong><div className="small muted">{WHAT[u.operation]}</div></td>
                  <td>{u.name}</td>
                  <td className="tabular" style={{ textAlign: 'right' }}>{num(u.day_produced ?? 0, decimals(u))} {u.measure}</td>
                  <td className="tabular" style={{ textAlign: 'right' }}><strong>{num(u.produced ?? 0, decimals(u))}</strong> {u.measure}</td>
                  <td className="tabular" style={{ textAlign: 'right' }}>
                    {u.target_to_date != null ? `${num(u.target_to_date, decimals(u))} ${u.measure}` : <span className="muted">no target</span>}
                    {u.target_to_date != null && u.target_basis === 'full_month' && <div className="small muted">whole month</div>}
                  </td>
                  <td className="tabular" style={{ textAlign: 'right' }}>
                    {u.target_month != null ? `${num(u.target_month, decimals(u))} ${u.measure}` : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {u.achievement_pct != null
                      ? <StatusChip tone={u.target_basis === 'full_month' ? 'grey' : (u.achievement_rag ?? 'grey')}>{pct(u.achievement_pct)} %</StatusChip>
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="small muted" style={{ margin: 'var(--sp-2) 0 0' }}>
        Month to {fmtDate(data.period?.to ?? to)}. Each row against its own target to date, on its site's working week.
        Targets are set under Targets (or in each module).
      </p>
    </Panel>
    </>
  );
}
